import React, { forwardRef, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import DraggableModal from 'chameleon/packages/blue-book-player/library/modal/DraggableModal';
import { calculateDefaults } from 'chameleon/packages/blue-book-player/InnerContainer/utils';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import Button from 'chameleon/packages/blue-book-player/library/button';
import styles from './LineReaderModal.css';

const LINE_READER_TOP_GAP = 104;
const blackListedClasses = ['crossout-container', 'sr-only'];

const LineReaderModal = forwardRef((props, ref) => {
  const { onClose, learnosityApp, questions } = props;
  const playerEl = useGetPlayerEl();
  const isManuallyPositioned = useRef(false);
  const [modalBoundingSettings, setModalBoundingSettings] = useState(
    calculateDefaults(
      learnosityApp?.getCurrentItem()?.reference,
      playerEl,
      blackListedClasses,
      LINE_READER_TOP_GAP,
    ),
  );
  const [isExpanded, setIsExpanded] = useState(
    !modalBoundingSettings?.hasTwoColumns,
  );
  const [isVisibleLineIncreasing, setIsVisibleLineIncreasing] = useState(true);
  const [visibleLineSize, setVisibleLineSize] = useState(1);
  const [showInstructions, setShowInstructions] = useState(true);

  useEffect(() => {
    const onResize = () => {
      const fixedInnerWidth = parseInt(window.innerWidth, 10);
      const newWidth = isExpanded
        ? fixedInnerWidth
        : parseInt(fixedInnerWidth / 2, 10);
      setModalBoundingSettings({
        ...modalBoundingSettings,
        width: newWidth,
        expandedWidth: fixedInnerWidth,
        collapsedWidth: parseInt(fixedInnerWidth / 2, 10),
      });
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [isExpanded]);

  useEffect(() => {
    const onItemChanging = (index) => {
      if (isManuallyPositioned.current) return;

      const question = Object.values(questions)?.[index];
      if (!question) return;

      const settings = calculateDefaults(
        question.learnosityItemReference,
        playerEl,
        blackListedClasses,
        LINE_READER_TOP_GAP,
      );
      setModalBoundingSettings(settings);
    };
    if (learnosityApp && playerEl && !isManuallyPositioned.current) {
      learnosityApp.on('item:changing', onItemChanging);
    }

    return () => {
      learnosityApp.off('item:changing', onItemChanging);
    };
  }, [learnosityApp, playerEl, isManuallyPositioned]);

  const {
    expandedWidth,
    collapsedWidth,
    width: defaultWidth,
    position: defaultPosition = {},
  } = modalBoundingSettings || {};

  const onVisibleLineResize = () => {
    setVisibleLineSize((currentVisibleLineSize) => {
      const newVisibleLineSize = isVisibleLineIncreasing
        ? currentVisibleLineSize + 1
        : currentVisibleLineSize - 1;

      if (newVisibleLineSize === 3) {
        setIsVisibleLineIncreasing(false);
      } else if (newVisibleLineSize === 1) {
        setIsVisibleLineIncreasing(true);
      }

      return newVisibleLineSize;
    });
  };

  const onToggleSize = (isExpanded, newWidth) => {
    setModalBoundingSettings({
      ...modalBoundingSettings,
      width: newWidth,
    });
    setIsExpanded(isExpanded);
  };

  const onDragStart = () => {
    isManuallyPositioned.current = true;
    setShowInstructions(false);
  };

  const wrapperStyles = {
    width: `${defaultWidth}px`,
    height: 'fit-content',
  };

  const accessibilityDragButton = {
    'aria-label': 'Move Line Reader',
    'aria-describedby': showInstructions
      ? 'line-reader-instruction1 line-reader-instruction2'
      : null,
  };

  const accessibilityCloseButton = {
    'aria-label': 'Close Line Reader',
  };

  const accessibilityResizeButton = (isExpanded) => {
    return {
      'aria-label': `${isExpanded ? 'reduce' : 'increase'} Line Reader size`,
    };
  };

  return (
    <DraggableModal
      defaultResetTo={{
        x: 0,
        y: defaultPosition.y,
      }}
      defaultPosition={defaultPosition}
      defaultWidth={defaultWidth}
      onClose={onClose}
      contentWithSpacing={false}
      isScrollableContent={false}
      contentClassName="h-full"
      size="custom"
      draggableClassName="z-blueBook_lineReader"
      wrapperStyles={wrapperStyles}
      expandedWidth={expandedWidth}
      collapsedWidth={collapsedWidth}
      onToggleSize={onToggleSize}
      onDragStart={onDragStart}
      accessibilityDragButton={accessibilityDragButton}
      accessibilityCloseButton={accessibilityCloseButton}
      accessibilityResizeButton={accessibilityResizeButton}
      ref={ref}
      focuslockDisabled
    >
      <div
        role="region"
        aria-label="Line reader"
        className="w-full h-full relative border border-[16px] border-[rgba(30,30,30,.92)]"
      >
        <div className="bg-[rgba(30,30,30,.92)] h-[40px] w-full text-bluebook-white1 text-[13px] text-center">
          {showInstructions ? (
            <div aria-hidden="true">
              <span id="line-reader-instruction1">Click</span> on the six dots,{' '}
              <span id="line-reader-instruction2">
                then use the arrow keys to move the line reader.
              </span>
            </div>
          ) : null}
        </div>
        <div
          className={cn('cursor-text opacity-0', {
            'h-[25px]': visibleLineSize === 1,
            'h-[45px]': visibleLineSize === 2,
            'h-[67px]': visibleLineSize === 3,
          })}
        />
        <div
          className={cn('relative w-full bg-[rgba(30,30,30,.92)]', {
            'h-[63px]': visibleLineSize === 1,
            'h-[43px]': visibleLineSize === 2,
            'h-[21px]': visibleLineSize === 3,
          })}
        >
          <div className="absolute -bottom-[8px] left-1/2 transform -translate-x-1/2">
            <Button
              size="custom"
              onClick={onVisibleLineResize}
              data-cy="change-line-size"
              className="m-auto relative bg-bluebook-black1/0 hover:bg-bluebook-light-gray w-[24px] h-[24px] rounded-md flex justify-center items-center"
              aria-label={
                isVisibleLineIncreasing
                  ? 'increase line size'
                  : 'decrease line size'
              }
            >
              <div
                className={cn(
                  `w-0 h-0 m-auto ${styles['line-reader-icon-down']}`,
                  {
                    'rotate-180': !isVisibleLineIncreasing,
                  },
                )}
              />
            </Button>
          </div>
        </div>
      </div>
    </DraggableModal>
  );
});

export default LineReaderModal;
